<template>
  <Modal
    :loading="loading"
    :modal-style="{ maxWidth: '540px' }"
    offset-menu
    :title="$t('modals.news_summary_subscription_modal.title')"
    @close="close"
  >
    <template v-if="error">
      <div class="field-error">
        {{ $t("modals.news_summary_subscription_modal.error_message") }}
      </div>
    </template>
    <template v-else-if="!loading">
      <div class="content q-mx-md">
        <div class="field-heading q-mb-sm">
          {{ $t("modals.news_summary_subscription_modal.delivery_heading") }}
        </div>
        <div class="field-subtext q-mb-sm">
          {{ $t("modals.news_summary_subscription_modal.delivery_message") }}
        </div>

        <QToggle
          v-model="newsSummarySubscription.mobile"
          class="full-width q-mt-sm justify-between"
          dense
          :label="
            $t('modals.news_summary_subscription_modal.toggle_mobile_label')
          "
          left-label
        />

        <div
          v-if="newsSummarySubscription.mobile && hasDigests"
          class="row q-mt-sm"
        >
          <Checkbox
            v-for="dailyDigest in digests"
            :key="`mobile-${dailyDigest}`"
            class="daily-digest"
            :label="$tm('digests')[dailyDigest]"
            :model-value="newsSummarySubscription.mobile_digests"
            new-design
            :value="dailyDigest"
            @update:model-value="setDigests('mobile_digests', dailyDigest)"
          />
        </div>

        <QToggle
          v-model="newsSummarySubscription.email"
          class="full-width q-mt-md justify-between"
          dense
          :label="
            $t('modals.news_summary_subscription_modal.toggle_email_label')
          "
          left-label
        />

        <div
          v-if="newsSummarySubscription.email && hasDigests"
          class="row q-mt-sm"
        >
          <Checkbox
            v-for="dailyDigest in digests"
            :key="`email-${dailyDigest}`"
            class="daily-digest"
            :label="$tm('digests')[dailyDigest]"
            :model-value="newsSummarySubscription.email_digests"
            new-design
            :value="dailyDigest"
            @update:model-value="setDigests('email_digests', dailyDigest)"
          />
        </div>

        <div
          v-if="newsSummarySubscription.email"
          class="q-mt-md"
        >
          <DistributedEmailInput
            v-model="newsSummarySubscription.recipients"
            new-design
          />
        </div>
      </div>
    </template>

    <template
      v-if="!error && !loading"
      #buttons
    >
      <BaseButton
        v-if="!$isMobile"
        :label="$t('global.cancel')"
        new-design
        @click.prevent="close"
      />
      <BaseButton
        color="primary"
        :disabled="loading"
        :label="$t('global.save')"
        new-design
        @click="save"
      />
    </template>
  </Modal>
</template>

<script>
import { storeToRefs } from "pinia";

import { BaseButton } from "shared/components/base";
import DistributedEmailInput from "shared/components/core/forms/DistributedEmailInput.vue";
import Modal from "shared/components/core/modals/Modal.vue";
import { useGlobalStore } from "shared/stores/global";
import { useUserStore } from "shared/stores/user";

export default {
  name: "NewsSummarySubscriptionModal",
  components: {
    DistributedEmailInput,
    Modal,
    BaseButton,
  },
  emits: ["save"],
  setup() {
    const userStore = useUserStore();
    const { currentUser } = storeToRefs(userStore);

    const globalStore = useGlobalStore();
    const { appConfig } = storeToRefs(globalStore);

    return { currentUser, appConfig };
  },
  data() {
    return {
      loading: false,
      error: false,
      newsSummarySubscription: {
        enabled: true,
        regular: true,
        featured: false,
        email: false,
        recipients: [],
        email_digests: [],
        mobile: false,
        mobile_digests: [],
      },
    };
  },
  computed: {
    digests() {
      return this.appConfig.digests;
    },
    hasDigests() {
      return this.digests.length;
    },
  },
  mounted() {
    this.loadNewsSummarySubscription();
  },
  methods: {
    setDigests(field, dailyDigest) {
      if (!this.newsSummarySubscription[field]) {
        this.newsSummarySubscription[field] = [];
      }

      const index = this.newsSummarySubscription[field].findIndex(
        (digest) => digest === dailyDigest
      );

      if (index < 0) {
        this.addDailyDigest(field, dailyDigest);
      } else {
        this.removeDailyDigest(field, index);
      }
    },
    addDailyDigest(field, val) {
      this.newsSummarySubscription[field].push(val);
    },
    removeDailyDigest(field, index) {
      this.newsSummarySubscription[field].splice(index, 1);
    },
    async loadNewsSummarySubscription() {
      try {
        const response = (
          await this.$streemApiV1.get(
            `users/${this.currentUser.id}/news_summary_subscription`
          )
        ).data;

        this.newsSummarySubscription = {
          ...this.newsSummarySubscription,
          ...response,
        };
      } catch {
        this.error = true;
      }
    },
    save() {
      const { mobile, email } = this.newsSummarySubscription;

      if (this.hasDigests) {
        if (mobile && !this.newsSummarySubscription.mobile_digests.length) {
          this.newsSummarySubscription.mobile = false;
        }

        if (email && !this.newsSummarySubscription.email_digests.length) {
          this.newsSummarySubscription.email = false;
        }
      }

      this.$emit("save", this.newsSummarySubscription);

      this.close();
    },
    close() {
      this.$modals.close("NewsSummarySubscriptionModal");
    },
  },
};
</script>

<style lang="scss" scoped>
.daily-digest {
  width: 140px;
}
</style>
